export type TipoOperacao = 'loja' | 'oficina'

export const TIPO_OPERACAO_DEFAULT: TipoOperacao = 'loja'

export interface VerticalLabels {
  /** Nome singular da unidade operacional (ex: "Avaliação", "Entrada") */
  labelPrincipal: string
  /** CTA para criar um novo registro (ex: "Nova avaliação") */
  novoRegistro: string
  /** Item de menu que lista os registros (ex: "Avaliações", "Veículos na oficina") */
  menuPrincipal: string
  /** Descrição da página de listagem (concordância correta em pt-BR) */
  descricaoListagem: string
  /** Nome do documento gerado ao fim do fluxo (ex: "Proposta", "Termo de entrada") */
  documentoFinal: string
  /** Texto curto exibido no card principal do dashboard */
  dashboardPrincipal: string
  /** Ação de iniciar (Avaliacoes.tsx getNextActionLabel) */
  iniciarAcao: string
  /** Ação de continuar */
  continuarAcao: string
  /** Ação de criar o documento de saída */
  criarDocumento: string
  /** Ação de visualizar o documento já salvo */
  verDocumento: string
  /** Helper de badge concluida ("Pronta para fechamento" / "Pronta para emissão de termo") */
  prontoParaDocumento: string
  /** Unidade plural para frases como "20 X por mês" */
  unidadeMensal: string
  /** Label do item de menu que lista os documentos finais (ex: "Fechamentos", "Serviços") */
  menuDocumentos: string
}

export interface VerticalConfig {
  tipo: TipoOperacao
  /** Rótulo amigável usado em onboarding e copy de marketing interna */
  rotulo: string
  /** Descrição curta usada no onboarding */
  descricao: string
  labels: VerticalLabels
  /** Flags reservadas para futuras evoluções; manter vazio quando não aplicável */
  features: Record<string, never>
  dashboard: {
    /** Título do painel ("Painel da Loja", "Painel da Oficina") */
    tituloPainel: string
    /** Card de KPI principal: label e descrição */
    cardPrincipalLabel: string
    /** Texto exibido na seção de registros recentes */
    secaoRecentes: string
    /** Label da contagem total */
    totalLabel: string
    /** Se false, o card de "Economia" não é renderizado (ex: oficina não tem cálculo de valor) */
    mostrarCardEconomia: boolean
    /** Se false, mini-cards "Reparos / Custo" dentro de cada item recente são ocultados */
    mostrarMetricasReparos: boolean
  }
  documentos: {
    /** Nome do documento final no singular */
    nomeOutput: string
    /** Título da página PropostaDetalhes ("Fechamento" / "Termo de Entrada") */
    tituloPagina: string
    /** Descrição abaixo do título em PropostaDetalhes */
    descricaoPagina: string
    /** Título da página PropostasConcluidas */
    tituloListagem: string
    /** Descrição da página PropostasConcluidas */
    descricaoListagem: string
    /** CTA do botão de salvar */
    ctaSalvar: string
    /** Texto enquanto salva */
    ctaSalvando: string
    /** Texto quando já salvou */
    ctaSalvo: string
    /** CTA do botão de gerar PDF */
    ctaGerar: string
    /** Texto enquanto gera PDF */
    ctaGerando: string
    /** Se false, esconde seções de Valor FIPE / Valor ofertado / Margem / Economia */
    mostrarValoresFinanceiros: boolean
    /** Texto curto descrevendo o formato exportado, usado em Planos.tsx */
    formatoArquivo: string
  }
  planos: {
    /** Descrição do plano básico em Planos.tsx */
    descricaoBasico: string
    /** Descrição do plano pro em Planos.tsx */
    descricaoPro: string
  }
  veiculos: {
    /** Descrição da página /veiculos */
    descricaoListagem: string
  }
  avaliacao: {
    /** Template de critérios usado pela engine compartilhada de Avaliacao360 */
    criteriosTemplate: 'completo' | 'oficina_leve'
    /** Se false, esconde custos e linguagem comercial de reparo */
    mostrarCustos: boolean
    /** Título principal da tela Avaliacao360 */
    tituloPagina: string
    /** Rótulo curto para pontos críticos/reprovados */
    labelCriticos: string
    /** Rótulo da aba/lista de pontos críticos com foto */
    labelFotosCriticas: string
    /** Texto do botão de finalização */
    ctaFinalizar: string
    /** Texto quando o registro já foi concluído */
    ctaFinalizado: string
  }
}

export const VERTICAIS: Record<TipoOperacao, VerticalConfig> = {
  loja: {
    tipo: 'loja',
    rotulo: 'Loja de veículos',
    descricao: 'Avaliação para compra, revenda e negociação.',
    labels: {
      labelPrincipal: 'Avaliação',
      novoRegistro: 'Nova avaliação',
      menuPrincipal: 'Avaliações',
      descricaoListagem: 'Visualize e gerencie todas as avaliações de veículos',
      documentoFinal: 'Proposta',
      dashboardPrincipal: 'Economia na compra',
      iniciarAcao: 'Iniciar avaliação',
      continuarAcao: 'Continuar avaliação',
      criarDocumento: 'Criar fechamento',
      verDocumento: 'Ver fechamento',
      prontoParaDocumento: 'Pronta para fechamento',
      unidadeMensal: 'avaliações',
      menuDocumentos: 'Fechamentos',
    },
    features: {},
    dashboard: {
      tituloPainel: 'Painel da Loja',
      cardPrincipalLabel: 'Economia na Compra',
      secaoRecentes: 'Avaliações Recentes',
      totalLabel: 'Total de Avaliações',
      mostrarCardEconomia: true,
      mostrarMetricasReparos: true,
    },
    documentos: {
      nomeOutput: 'Proposta',
      tituloPagina: 'Fechamento',
      descricaoPagina: 'Revise a avaliação e gere o documento final',
      tituloListagem: 'Fechamentos Concluídos',
      descricaoListagem: 'Acompanhe os fechamentos gerados a partir das suas avaliações',
      ctaSalvar: 'Salvar fechamento',
      ctaSalvando: 'Salvando...',
      ctaSalvo: 'Fechamento salvo',
      ctaGerar: 'Gerar documento final',
      ctaGerando: 'Gerando documento...',
      mostrarValoresFinanceiros: true,
      formatoArquivo: 'Fechamentos em PDF',
    },
    planos: {
      descricaoBasico: 'Ideal para autônomos',
      descricaoPro: 'Ideal para lojas e revendas',
    },
    veiculos: {
      descricaoListagem: 'Gerencie todos os veículos cadastrados no sistema',
    },
    avaliacao: {
      criteriosTemplate: 'completo',
      mostrarCustos: true,
      tituloPagina: 'Avaliação 360°',
      labelCriticos: 'Reprovados',
      labelFotosCriticas: 'Reparos',
      ctaFinalizar: 'Finalizar avaliação',
      ctaFinalizado: 'Avaliação finalizada',
    },
  },
  oficina: {
    tipo: 'oficina',
    rotulo: 'Oficina',
    descricao: 'Registro de entrada com fotos, checklist e proteção contra conflitos.',
    labels: {
      labelPrincipal: 'Entrada',
      novoRegistro: 'Nova entrada',
      menuPrincipal: 'Entradas',
      descricaoListagem: 'Veículos com entrada registrada e seu estado documentado',
      documentoFinal: 'Termo de entrada',
      dashboardPrincipal: 'Proteção na entrada',
      iniciarAcao: 'Iniciar entrada',
      continuarAcao: 'Continuar entrada',
      criarDocumento: 'Emitir termo',
      verDocumento: 'Ver termo',
      prontoParaDocumento: 'Pronta para emissão de termo',
      unidadeMensal: 'entradas',
      menuDocumentos: 'Termos de Entrada',
    },
    features: {},
    dashboard: {
      tituloPainel: 'Painel da Oficina',
      cardPrincipalLabel: 'Proteção na Entrada',
      secaoRecentes: 'Entradas Recentes',
      totalLabel: 'Total de Entradas',
      mostrarCardEconomia: false,
      mostrarMetricasReparos: false,
    },
    documentos: {
      nomeOutput: 'Termo de entrada',
      tituloPagina: 'Termo de Entrada',
      descricaoPagina: 'Revise a entrada e emita o termo final para assinatura do cliente',
      tituloListagem: 'Termos Emitidos',
      descricaoListagem: 'Termos de entrada já emitidos com assinatura registrada',
      ctaSalvar: 'Salvar termo',
      ctaSalvando: 'Salvando...',
      ctaSalvo: 'Termo salvo',
      ctaGerar: 'Emitir termo de entrada',
      ctaGerando: 'Emitindo termo...',
      mostrarValoresFinanceiros: false,
      formatoArquivo: 'Termos de entrada em PDF',
    },
    planos: {
      descricaoBasico: 'Ideal para oficinas independentes',
      descricaoPro: 'Ideal para oficinas e centros automotivos',
    },
    veiculos: {
      descricaoListagem: 'Veículos atualmente na oficina e seu histórico de entradas',
    },
    avaliacao: {
      criteriosTemplate: 'oficina_leve',
      mostrarCustos: false,
      tituloPagina: 'Entrada do veículo',
      labelCriticos: 'Danos',
      labelFotosCriticas: 'Danos',
      ctaFinalizar: 'Finalizar entrada',
      ctaFinalizado: 'Entrada finalizada',
    },
  },
}

export function isTipoOperacao(value: unknown): value is TipoOperacao {
  return value === 'loja' || value === 'oficina'
}

export function getVerticalConfig(tipo: TipoOperacao | string | null | undefined): VerticalConfig {
  return isTipoOperacao(tipo) ? VERTICAIS[tipo] : VERTICAIS[TIPO_OPERACAO_DEFAULT]
}
